import { Element, Transforms, Editor, Range, Node, Path } from 'slate';
import { isSimpleBlockActive } from 'app/slate/functions';
import { useRef, useState, useEffect, useCallback, useContext } from 'react';
import { refHandler } from 'app/utils/helper-functions';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { useCoverageScope } from 'app/components/editor/coverage/CoverageScopeContext';
import { useStyles } from 'app/api/style-queries';
import { UserContext } from 'app/state/contexts';
import { useSlate, ReactEditor } from 'slate-react';
import { SidebarContext } from 'app/state/contexts/SidebarContext';
import { useSelector } from 'react-redux';

import { useParams } from 'react-router-dom';
import { useGetCurrentLock } from 'app/views/documents/document-viewers/document-editor/lock-queries';
import { useEditorMode } from 'app/state/contexts/EditorContext';
const isStyledParagraph = (node) => Element.isElement(node) && node?.type === 'styled-paragraph';

const withStyledParagraph =
  (options = {}) =>
  (editor) => {
    const { insertBreak, renderElement, isBlock } = editor;
    const { isPDF } = options;
    editor.isBlock = (element) => isStyledParagraph(element) || isBlock(element);

    editor.renderElement = (props) => {
      if (isStyledParagraph(props.element)) {
        return <StyledParagraphElement {...props} isPDF={isPDF} />;
      } else {
        return renderElement(props);
      }
    };

    editor.insertBreak = () => {
      const { selection } = editor;
      if (!selection || !Range.isCollapsed(selection)) {
        return insertBreak();
      }
      const [itemEntry] = Editor.nodes(editor, {
        match: isStyledParagraph,
      });
      if (!itemEntry) {
        return insertBreak();
      }
      const [styledParagraphNode, styledParagraphPath] = itemEntry;

      const currentLinePath = selection.anchor.path;
      const relativePath = currentLinePath.slice(styledParagraphPath.length);

      const currentLineNode = relativePath.reduce((node, idx) => (node && node.children ? node.children[idx] : null), styledParagraphNode);
      const isCurrentLineEmpty = currentLineNode && currentLineNode.text === '';

      if (isCurrentLineEmpty) {
        return Editor.withoutNormalizing(editor, () => {
          const parentPath = Path.parent(styledParagraphPath);
          const parentNode = Node.get(editor, parentPath);
          const insertIndex = styledParagraphPath[styledParagraphPath.length - 1] + 1;
          const insertPath = [...parentPath, insertIndex];
          if (insertIndex <= parentNode.children.length) {
            Transforms.insertNodes(editor, { type: 'paragraph', children: [{ text: '' }] }, { at: insertPath });
            Transforms.select(editor, Editor.start(editor, insertPath));
          }
        });
      }
      return insertBreak();
    };

    return editor;
  };

export const StyledParagraphElement = ({ attributes, children, element, isPDF }) => {
  const { scopeUuids, missingUuids } = useCoverageScope();
  let coverageClass = '';
  if (element.uuid) {
    if (missingUuids.has(element.uuid)) coverageClass = ' coverage-missing';
    else if (scopeUuids.has(element.uuid)) coverageClass = ' coverage-scope';
  }

  const { documentId, sectionId } = useParams();
  const ref = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const startMouseRef = useRef({ x: 0, y: 0 });
  const startPosRef = useRef({ x: 0, y: 0 });
  const dimensionsRef = useRef({ width: 0, height: 0 });
  const { pDocument, activeSection } = useEditorSelector((editor) => editor);
  const hasWritePermission = useSelector((state) => state.document.sectionPermissions)?.[sectionId] === 'WRITE';
  const { data: lock, isLoading: isLockLoading } = useGetCurrentLock(documentId, sectionId);
  const isDirty = useEditorSelector((editor) => editor.isDirty);
  const editor = useSlate();
  const pageStyleId = pDocument.content.design.find((d) => d.pageId === activeSection.uuid)?.styleId;
  const { selectedOrganization } = useContext(UserContext);
  const sidebarContext = useContext(SidebarContext);
  const { data: stylesData = [] } = useStyles({
    organization: selectedOrganization,
    documentRevisionId: pDocument.revisionId,
    styleIds: [pageStyleId],
  });
  const styledParagraph = sidebarContext?.styledParagraph;
  const setStyledParagraph = sidebarContext?.setStyledParagraph;
  const setIsSidebarOpen = sidebarContext?.setIsSidebarOpen;
  const { isTaggingMode } = useEditorMode();
  const [pageStyle] = stylesData;
  const nodeStyle = pageStyle?.content?.[`xrp-super-id-${element?.id}`];
  const isActive = styledParagraph?.id === element.id;

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const positionRef = useRef({ x: 0, y: 0 }); // Ref to track latest position for event handlers
  const [positionInitialized, setPositionInitialized] = useState(false);
  const [hasUnsavedDrag, setHasUnsavedDrag] = useState(false);

  useEffect(() => {
    Transforms.setNodes(
      editor,
      { connected: true },
      {
        at: [], // apply at the root
        match: (node) => Element.isElement(node) && node.type === 'styled-paragraph',
        mode: 'all',
      }
    );
  }, [editor]);

  useEffect(() => {
    if (isDragging) return; // Don't reset position while dragging
    const savedLeft = nodeStyle?.['left']?.value;
    const savedTop = nodeStyle?.['top']?.value;
    const hasPosition = savedLeft !== undefined && savedTop !== undefined;

    if (hasPosition) {
      setPosition({ x: savedLeft, y: savedTop });
      setPositionInitialized(true);
      setHasUnsavedDrag(false); // Position was saved, clear the flag
    } else {
      setPositionInitialized(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeStyle]);

  // Initialize dimensions from saved nodeStyle or element's rendered size
  useEffect(() => {
    if (!ref.current) return;
    const savedWidth = nodeStyle?.['width']?.value;
    const savedHeight = nodeStyle?.['height']?.value;
    const rect = ref.current.getBoundingClientRect();
    dimensionsRef.current = {
      width: savedWidth !== undefined ? savedWidth : rect.width,
      height: savedHeight !== undefined ? savedHeight : rect.height,
    };
  }, [nodeStyle]);

  const isDraggable = !(styledParagraph?.connected ?? element?.connected) && !isDirty;
  const handleMouseDown = (e) => {
    const selectedNode = isInsideStyledParagraph(editor);
    if (!selectedNode || selectedNode.id !== element.id) {
      const path = ReactEditor.findPath(editor, element);
      Transforms.select(editor, path);
      return;
    }
    if (!isDraggable || !ref.current) return;
    e.preventDefault();
    startMouseRef.current = { x: e.clientX, y: e.clientY };
    startPosRef.current = { x: position.x, y: position.y };
    // Use saved dimensions from nodeStyle if user has manually set them, otherwise use rendered dimensions
    const savedWidth = nodeStyle?.['width']?.value;
    const savedHeight = nodeStyle?.['height']?.value;
    const rect = ref.current.getBoundingClientRect();
    dimensionsRef.current = {
      width: savedWidth !== undefined ? savedWidth : rect.width,
      height: savedHeight !== undefined ? savedHeight : rect.height,
    };
    if (!positionInitialized) {
      setPositionInitialized(true);
    }
    setIsDragging(true);
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      const deltaX = e.clientX - startMouseRef.current.x;
      const deltaY = e.clientY - startMouseRef.current.y;
      let newX = startPosRef.current.x + deltaX;
      let newY = startPosRef.current.y + deltaY;

      if (ref.current) {
        const pageContent = ref.current.closest('.page-content');
        if (pageContent) {
          const containerRect = pageContent.getBoundingClientRect();
          const elementRect = ref.current.getBoundingClientRect();
          const minX = 0;
          const minY = 0;
          const maxX = containerRect.width - elementRect.width;
          const maxY = containerRect.height - elementRect.height;
          newX = Math.max(minX, Math.min(newX, maxX));
          newY = Math.max(minY, Math.min(newY, maxY));
        }
      }

      positionRef.current = { x: newX, y: newY }; // Update ref for event handlers
      setPosition({ x: newX, y: newY });
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      setHasUnsavedDrag(true); // Keep position until saved
      // Pass the dragged position to context so sidebar can save it
      // Use positionRef to get the latest position value (avoids stale closure)
      setStyledParagraph &&
        setStyledParagraph((prev) => ({
          ...prev,
          pendingPosition: { top: positionRef.current.y, left: positionRef.current.x },
          pendingDimensions: { width: dimensionsRef.current.width, height: dimensionsRef.current.height },
        }));
    }
    //save();
  }, [isDragging, setStyledParagraph]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div
      {...attributes}
      ref={refHandler(attributes, ref)}
      style={{
        cursor: isDraggable ? (isDragging ? 'grabbing' : 'grab') : 'text',
        // Apply position via inline styles during dragging or when there are unsaved drag changes
        ...((isDragging || hasUnsavedDrag) && {
          position: 'absolute',
          top: `${position.y}px`,
          left: `${position.x}px`,
          width: `${dimensionsRef.current.width}px`,
          height: `${dimensionsRef.current.height}px`,
        }),
        ...(isDragging && { willChange: 'top, left' }),
      }}
      contentEditable={!isDraggable && lock?.isValidForUser && lock?.isValidNow && !isLockLoading && hasWritePermission}
      suppressContentEditableWarning={true}
      onMouseDown={isDraggable ? handleMouseDown : undefined}
      className={`xrp-super-id-${element.id} ${(isActive || isDragging) && isInsideStyledParagraph(editor) ? 'styled-paragraph--active' : ''}${coverageClass}`}
      onClick={
        isTaggingMode
          ? undefined
          : () => {
              setIsSidebarOpen(7);
              // Preserve pending position/dimensions when updating the selected element
              setStyledParagraph &&
                setStyledParagraph((prev) => ({
                  ...element,
                  pendingPosition: prev?.pendingPosition,
                  pendingDimensions: prev?.pendingDimensions,
                }));
            }
      }
    >
      {children}
    </div>
  );
};

export const insertStyledParagraph = (editor, uuid) => {
  const isActive = isSimpleBlockActive(editor, 'styled-paragraph');

  Transforms.unwrapNodes(editor, {
    match: (n) => !Editor.isEditor(n) && Element.isElement(n) && n?.type === 'styled-paragraph',
    split: true,
  });

  if (!editor.selection) return;

  Transforms.setNodes(
    editor,
    { type: 'paragraph' },
    {
      at: editor.lastSelection,
    }
  );

  if (!isActive) {
    const block = { type: 'styled-paragraph', children: [], id: uuid, connected: true };
    Transforms.wrapNodes(editor, block);
  }
};

export const unWrapStyledParagraph = (editor, id) => {
  const [match] = Editor.nodes(editor, {
    match: (n) => n?.type === 'styled-paragraph' && n.id === id,
  });

  if (!match) return;

  const [, path] = match;
  Transforms.unwrapNodes(editor, {
    at: path,
    match: (n) => n?.type === 'styled-paragraph',
  });
};

export const isInsideStyledParagraph = (editor) => {
  if (!editor.selection) return null;
  const [match] = Editor.nodes(editor, {
    match: (n) => n?.type === 'styled-paragraph',
  });
  return match ? match[0] : null;
};

export const toggleConnect = (editor, id, currentStyle) => {
  const [match] = Editor.nodes(editor, {
    match: (n) => n?.type === 'styled-paragraph' && n.id === id,
  });

  if (match) {
    const [, path] = match;
    const [node] = Editor.node(editor, path);
    const connected = node.connected ?? true;

    if (connected) {
      Transforms.setNodes(editor, { connected: false, currentStyle }, { at: path });
    } else {
      Transforms.setNodes(editor, { connected: true, currentStyle }, { at: path });
    }
  }
};

export default withStyledParagraph;
